import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, type Column, type ContextMenuItem, Badge, TableSkeleton, CoreVenueModal } from '../components/Common';
import { dataCorrectionsAPI, adminAPI } from '../api/endpoints';
import type { DataCorrection, AdminCoreVenue, AdminCoreVenueUpdate } from '../types';
import { format } from 'date-fns';
import { Check, X, Wrench, Clock, CheckCircle, XCircle, Pencil } from 'lucide-react';

type CorrectionStatus = DataCorrection['status'];

const STATUS_CONFIG: Record<CorrectionStatus, { label: string; icon: typeof Clock; colorClass: string; borderClass: string; badgeVariant: 'warning' | 'success' | 'error' | 'info' }> = {
  pending_review: { label: 'Pending Review', icon: Clock, colorClass: 'text-amber-400', borderClass: 'border-amber-500/20', badgeVariant: 'warning' },
  accepted: { label: 'Accepted', icon: CheckCircle, colorClass: 'text-green-400', borderClass: 'border-green-500/20', badgeVariant: 'success' },
  refused: { label: 'Refused', icon: XCircle, colorClass: 'text-red-400', borderClass: 'border-red-500/20', badgeVariant: 'error' },
  corrected: { label: 'Corrected', icon: Wrench, colorClass: 'text-primary-400', borderClass: 'border-primary-500/20', badgeVariant: 'info' },
};

const STATUS_ORDER: CorrectionStatus[] = ['pending_review', 'accepted', 'corrected', 'refused'];

const DATA_TYPE_LABELS: Record<string, string> = {
  venue_core: 'Venue (Core)',
  venue_note: 'Venue (Note)',
  contact: 'Contact',
};

const CORRECTION_TYPE_LABELS: Record<string, string> = {
  wrong: 'Wrong',
  spam: 'Spam',
  expired: 'Expired',
  inappropriate: 'Inappropriate',
};

export function CorrectionsPage() {
  const queryClient = useQueryClient();
  const [editingVenue, setEditingVenue] = useState<AdminCoreVenue | null>(null);

  const { data: corrections = [], isLoading } = useQuery({
    queryKey: ['admin', 'corrections'],
    queryFn: async () => {
      const response = await dataCorrectionsAPI.adminListAll();
      return response.data.items;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CorrectionStatus }) =>
      dataCorrectionsAPI.adminUpdateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'corrections'] });
    },
  });

  const handleEditVenue = async (objectId: string) => {
    const response = await adminAPI.getCoreVenue(objectId);
    setEditingVenue(response.data);
  };

  const handleVenueSubmit = async (venueId: string, data: AdminCoreVenueUpdate) => {
    await adminAPI.updateCoreVenue(venueId, data);
    queryClient.invalidateQueries({ queryKey: ['admin', 'corrections'] });
  };

  const groupedCorrections = useMemo(() => {
    const groups: Record<CorrectionStatus, DataCorrection[]> = {
      pending_review: [],
      accepted: [],
      refused: [],
      corrected: [],
    };
    for (const correction of corrections) {
      groups[correction.status].push(correction);
    }
    return groups;
  }, [corrections]);

  const getContextMenu = useCallback((row: DataCorrection): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];
    if (row.data_type === 'venue_core' || row.data_type === 'venue_note') {
      items.push({
        label: 'Edit Venue',
        icon: <Pencil className="w-4 h-4" />,
        onClick: () => handleEditVenue(row.object_id),
      });
    }
    if (row.status !== 'accepted') {
      items.push({
        label: 'Accept',
        icon: <Check className="w-4 h-4" />,
        onClick: () => updateStatusMutation.mutate({ id: row._id, status: 'accepted' }),
      });
    }
    if (row.status !== 'corrected') {
      items.push({
        label: 'Mark Corrected',
        icon: <Wrench className="w-4 h-4" />,
        onClick: () => updateStatusMutation.mutate({ id: row._id, status: 'corrected' }),
      });
    }
    if (row.status !== 'refused') {
      items.push({
        label: 'Refuse',
        icon: <X className="w-4 h-4" />,
        onClick: () => updateStatusMutation.mutate({ id: row._id, status: 'refused' }),
        danger: true,
      });
    }
    if (row.status !== 'pending_review') {
      items.push({
        label: 'Back to Pending',
        icon: <Clock className="w-4 h-4" />,
        onClick: () => updateStatusMutation.mutate({ id: row._id, status: 'pending_review' }),
      });
    }
    return items;
  }, [updateStatusMutation]);

  const columns: Column<DataCorrection>[] = [
    {
      key: 'type',
      label: 'Type',
      width: '120px',
      render: (row) => {
        const typeLabel = CORRECTION_TYPE_LABELS[row.type] || row.type;
        const variant = row.type === 'spam' || row.type === 'inappropriate' ? 'error' : 'warning';
        return <Badge variant={variant}>{typeLabel}</Badge>;
      },
    },
        {
      key: 'data_type',
      label: 'Data Type',
      width: '130px',
      render: (row) => (
        <span className="text-dark-text-secondary text-sm">
          {DATA_TYPE_LABELS[row.data_type] || row.data_type}
        </span>
      ),
    },
        {
      key: 'venue_name',
      label: 'Venue',
      sortable: true,
      width: '220px',
      render: (row) => {
        if (!row.venue_name) {
          return <span className="text-dark-text-muted">-</span>;
        }
        return (
          <div>
            <div className="font-medium">{row.venue_name}</div>
            {row.venue_address && (
              <div className="text-xs text-dark-text-muted">{row.venue_address}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'invalid_fields',
      label: 'Fields',
      render: (row) => {
        if (row.invalid_fields.length === 0 && !row.precisions) {
          return <span className="text-dark-text-muted">-</span>;
        }
        return (
          <div className="space-y-1">
            {row.invalid_fields.map((f, i) => (
              <div key={i} className="text-sm">
                <span className="font-medium text-dark-text-primary">{f.field}</span>
                {f.current_value && (
                  <span className="text-red-400 ml-2 line-through">{f.current_value}</span>
                )}
                {f.suggested_value && (
                  <span className="text-green-400 ml-2">{f.suggested_value}</span>
                )}
              </div>
            ))}
            {row.precisions && (
              <div className="text-xs text-dark-text-muted italic">{row.precisions}</div>
            )}
          </div>
        );
      },
    },

    {
      key: 'created_at',
      label: 'Reported',
      sortable: true,
      width: '140px',
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium">{format(new Date(row.created_at), 'MMM d, yyyy')}</div>
          <div className="text-xs text-dark-text-muted">{format(new Date(row.created_at), 'h:mm a')}</div>
        </div>
      ),
    },
  ];

  const totalCount = corrections.length;
  const pendingCount = groupedCorrections.pending_review.length;

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-6 flex-1 flex flex-col min-h-0">
        <div className="mb-6 animate-slideDown">
          <h1 className="text-2xl font-bold text-dark-text-primary mb-2 tracking-tight">
            Corrections
          </h1>
          <p className="text-sm text-dark-text-secondary">
            {pendingCount > 0 && (
              <span className="text-amber-400 font-medium">{pendingCount} pending review</span>
            )}
            {pendingCount > 0 && totalCount > pendingCount && ' · '}
            {totalCount > pendingCount && (
              <span>{totalCount - pendingCount} processed</span>
            )}
            {totalCount === 0 && 'No corrections reported'}
          </p>
        </div>

        {isLoading ? (
          <div className="card animate-scaleIn mb-4">
            <TableSkeleton rows={5} />
          </div>
        ) : totalCount === 0 ? (
          <div className="card animate-scaleIn mb-4 p-8 text-center text-dark-text-muted">
            No corrections reported yet.
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-4">
            {STATUS_ORDER.map((statusKey) => {
              const items = groupedCorrections[statusKey];
              if (items.length === 0) return null;
              const config = STATUS_CONFIG[statusKey];
              const StatusIcon = config.icon;
              return (
                <div key={statusKey} className="animate-scaleIn">
                  <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
                    <StatusIcon className={`w-4 h-4 ${config.colorClass}`} />
                    <span className={`text-sm font-semibold ${config.colorClass}`}>{config.label}</span>
                    <span className="text-xs text-dark-text-muted">({items.length})</span>
                  </div>
                  <div className={`card border ${config.borderClass}`}>
                    <Table
                      data={items}
                      columns={columns}
                      searchable={false}
                      emptyMessage=""
                      rowContextMenu={getContextMenu}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CoreVenueModal
        isOpen={editingVenue !== null}
        onClose={() => setEditingVenue(null)}
        onSubmit={handleVenueSubmit}
        venue={editingVenue}
      />
    </div>
  );
}
